import _startCase from "lodash/startCase";
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { VirtualizedTable } from 'core/components/table';
import { getDimensionDisplayLabel, getDimensions } from '@kentik/ui-shared/query/query';
import { getMeasurementDimension } from '@kentik/ui-shared/query/measurements';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import { observer } from 'mobx-react';
import { renderDimensionValue, renderMetricValue } from './legendUtils';
const Wrapper = styled.div.withConfig({
  displayName: "UdeTable__Wrapper",
  componentId: "sc-darhtj-0"
})(["&&{display:flex;flex-direction:column;flex:1 0 100%;.tbody{.tr{.td{transition:opacity 0.2s ease;}&.isCharted:not(.isVisible),&.", "{&:not(:hover) .td{opacity:0.4;}}}}}"], Classes.DISABLED);
const ColorIndicator = styled.div.withConfig({
  displayName: "UdeTable__ColorIndicator",
  componentId: "sc-darhtj-1"
})(["&&{display:inline-block;font-size:16px;font-weight:bold;line-height:1;transition:transform 0.1s ease;cursor:pointer;&:hover{transform:scale(1.3);}}"]);
const SeriesColor = observer(({
  model
}) => {
  const {
    queryModel
  } = model.results;
  const color = queryModel.getSeriesColor(model.id);
  if (!color) {
    return null;
  }
  return /*#__PURE__*/React.createElement(ColorIndicator, {
    style: {
      color
    }
  }, queryModel.isSeriesVisible(model.id) ? '\u2501' : '\u2500');
});
function getRowClass(model) {
  const {
    queryModel
  } = model.results;
  const isTableOnly = queryModel.settings.chart.type === 'table';
  const isVisible = !isTableOnly && queryModel.isSeriesVisible(model.id);
  const isCharted = !isTableOnly && queryModel.isRowCharted(model.id);
  const isDisabled = !isTableOnly && !queryModel.isRowCharted(model.id);
  return classNames({
    isTableOnly,
    isVisible,
    isCharted,
    [Classes.DISABLED]: isDisabled
  });
}
function getColumns(queryModel, measurement, rollups) {
  const {
    query,
    settings
  } = queryModel;
  const isTableOnly = settings.chart.type === 'table';
  const position = isTableOnly ? 'bottom' : settings.chart.legend?.position || 'bottom';
  const showRollups = isTableOnly || (settings?.chart?.legend?.showRollups ?? true);
  const cols = [];
  if (!isTableOnly) {
    cols.push({
      name: 'color',
      label: '',
      width: position === 'right' ? 28 : 36,
      padding: '6px',
      ellipsis: false,
      sortable: false,
      renderer: ({
        model
      }) => /*#__PURE__*/React.createElement(SeriesColor, {
        model: model
      })
    });
  }
  getDimensions(query).forEach(dimension => {
    const info = getMeasurementDimension(measurement, dimension.name);
    const baseLabel = info?.Label || _startCase(dimension.name);
    cols.push({
      name: `dimensions.${dimension.name}`,
      label: getDimensionDisplayLabel(dimension.name, baseLabel),
      sortable: false,
      renderer: ({
        model
      }) => renderDimensionValue(dimension.name, model.get(), query, measurement)
    });
  });
  if (showRollups) {
    rollups.forEach(rollup => {
      const metricLabel = rollup.metricInfo?.Label || rollup.metric;
      const rollupLabel = `${metricLabel} (${_startCase(rollup.fn)})`;
      cols.push({
        name: `rollups.${rollup.key}`,
        label: rollupLabel,
        sortable: false,
        align: 'right',
        renderer: ({
          value
        }) => renderMetricValue(rollup.metric, value, rollup.metricInfo, query.measurement)
      });
    });
  }
  return cols;
}
function UdeTable({
  queryModel,
  measurement,
  rollups,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave
}) {
  const {
    results,
    settings
  } = queryModel;
  const isTableOnly = settings.chart.type === 'table';
  const columns = useMemo(() => getColumns(queryModel, measurement, rollups), [measurement, queryModel, rollups]);
  const handleRowClick = (model, evt) => {
    if (queryModel.isRowCharted(model.id)) {
      const rowIndex = results.rows.findIndex(r => r.id === model.id);
      onRowClick(model.id, rowIndex, evt);
    }
  };
  const handleRowMouseOver = model => {
    onRowMouseEnter(model.id);
  };
  const handleRowMouseOut = () => {
    onRowMouseLeave();
  };
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(VirtualizedTable, {
    flexed: true,
    collection: results,
    columns: columns,
    rowHeight: 30,
    rowClass: getRowClass,
    selectOnRowClick: false,
    onRowClick: !isTableOnly ? handleRowClick : undefined,
    onRowMouseOver: !isTableOnly ? handleRowMouseOver : undefined,
    onRowMouseOut: !isTableOnly ? handleRowMouseOut : undefined
  }));
}
export default UdeTable;